/**
 * API client for canvas version control endpoints (B2 backport).
 *
 * Mirrors the REST surface exposed by
 * apps/api/src/ponpon_api/routes/versions.py:
 *
 *   GET    /projects/:id/canvas/versions               → listVersions
 *   POST   /projects/:id/canvas/versions               → createVersion
 *   POST   /projects/:id/canvas/versions/:vid/restore  → restoreVersion
 *   DELETE /projects/:id/canvas/versions/:vid          → deleteVersion
 *   GET    /projects/:id/canvas/versions/diff?...      → diffVersions
 *
 * NOTE: restore + delete + create require write access — `apiRequest`
 * sends auth headers automatically. A 409 from any endpoint means the
 * project room isn't active; the caller should prompt the user to
 * open the project in the console first.
 */

import { apiRequest } from './client'

export interface VersionEntry {
  version_id: string
  label: string
  created_by: string
  created_at: string
  parents: string[]
}

export interface VersionListResponse {
  head: string | null
  versions: VersionEntry[]
}

export interface RestoreVersionResponse {
  restored: boolean
  version_id: string
}

export interface DeleteResponse {
  deleted: string[]
}

export interface DiffResponse {
  diff: string
}

export async function listVersions(projectId: string): Promise<VersionListResponse> {
  return apiRequest<VersionListResponse>(`/projects/${projectId}/canvas/versions`)
}

export async function createVersion(
  projectId: string,
  label: string,
): Promise<VersionEntry> {
  return apiRequest<VersionEntry>(`/projects/${projectId}/canvas/versions`, {
    method: 'POST',
    body: JSON.stringify({ label }),
  })
}

export async function restoreVersion(
  projectId: string,
  versionId: string,
): Promise<RestoreVersionResponse> {
  return apiRequest<RestoreVersionResponse>(
    `/projects/${projectId}/canvas/versions/${encodeURIComponent(versionId)}/restore`,
    { method: 'POST' },
  )
}

export async function deleteVersion(
  projectId: string,
  versionId: string,
): Promise<DeleteResponse> {
  return apiRequest<DeleteResponse>(
    `/projects/${projectId}/canvas/versions/${encodeURIComponent(versionId)}`,
    { method: 'DELETE' },
  )
}

export async function diffVersions(
  projectId: string,
  versionA: string,
  versionB: string,
): Promise<DiffResponse> {
  const qs = new URLSearchParams({ version_a: versionA, version_b: versionB }).toString()
  return apiRequest<DiffResponse>(
    `/projects/${projectId}/canvas/versions/diff?${qs}`,
  )
}
